import React, {Fragment, useState} from 'react';

import {Drawer} from 'presentation/components/Drawer';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import OurDoctorDesktopImage from 'presentation/assets/images/ourDoctorDesktop.webp';
import OurDoctorMobileImage from 'presentation/assets/images/ourDoctorMobile.webp';
import Icon from 'presentation/icons';
import {HandleMessage} from 'presentation/components/HandleMessage';
import {useGetFaqUrlQuery} from 'presentation/pages/private/help/queries/useGetFaqUrlQuery';
import {IFrameModal} from 'presentation/components/IFrameModal';
import {Snackbar} from 'presentation/components/Snackbar';
import {useHostName} from 'presentation/hooks';
import {usePostOurDoctorMessageMutation} from 'presentation/mutation/usePostOurDoctorMessageMutation';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {OurDoctorHomeDrawerProps} from './types';
import {StyledIconButton, StyledImage} from './styles';

const {agreementText, needHelp, notShowAgain, tryAgainLater} = translations['pt-br'].commons;
const {
	homeDrawer: {description1, description2, description3, description4, imageAlt},
} = translations['pt-br'].privateFlow.ourDoctor;
const {faq} = translations['pt-br'].privateFlow.help;

export function OurDoctorHomeDrawer({isOpen}: OurDoctorHomeDrawerProps) {
	const queryClient = useQueryClient();
	const {isMobile} = useMediaQueries();
	const {isGndiSaoPaulo} = useHostName();
	const [checkboxState, setCheckboxState] = useState(false);
	const {data: faqData, refetch: getFaqData, isLoading: isFaqLoading} = useGetFaqUrlQuery();
	const [openFaqIframe, setOpenFaqIFrame] = useState(false);
	const [isErrorState, setIsErrorState] = useState(false);
	const {mutateAsync: handleOurDoctorAgree, isPending: isAgreementActionLoading} =
		usePostOurDoctorMessageMutation();

	const handleCloseDrawer = () => {
		queryClient.setQueryData([QUERY_KEYS.OUR_DOCTOR_MESSAGE], {visualizou: true});
	};

	const handleGetHelp = async () => {
		const {data, isError} = await getFaqData();
		if (isError) {
			setIsErrorState(true);
			return;
		}
		if (data?.url) {
			if (isGndiSaoPaulo) {
				setOpenFaqIFrame(true);
			} else {
				window.open(data.url, '_blank');
			}
		}
	};

	const onToggleCheckbox = () => {
		setCheckboxState(prev => !prev);
	};

	const handleConfirm = async () => {
		if (!checkboxState) {
			handleCloseDrawer();
			return;
		}

		try {
			await handleOurDoctorAgree();
			handleCloseDrawer();
		} catch {
			setIsErrorState(true);
		}
	};

	return (
		<Fragment>
			<Drawer.Root
				open={isOpen}
				onClose={handleCloseDrawer}
				onOpen={() => {}}
				width="600px"
				customMaxHeight="100%"
				pullerPosition="absolute"
				sx={{zIndex: 9000, position: 'relative'}}>
				<StyledImage
					src={isMobile ? OurDoctorMobileImage : OurDoctorDesktopImage}
					alt={imageAlt}
				/>

				<StyledIconButton
					size="small"
					onClick={handleCloseDrawer}
					sx={{position: 'absolute', top: 16, right: 16}}>
					<Icon name="close-default" />
				</StyledIconButton>

				<Drawer.Content mt={3} mb={2}>
					<Typography fontSize={20} fontWeight={600}>
						{description1}
					</Typography>
					<Typography fontWeight={600} mb={3}>
						{description2}
					</Typography>
					<Typography>
						<HandleMessage message={description3} />
					</Typography>
					<Typography mt={2}>{description4}</Typography>
				</Drawer.Content>
				<Drawer.Footer
					secondButtonTitle={agreementText}
					secondButtonOnClick={handleConfirm}
					secondButtonDisabled={isAgreementActionLoading}
					secondButtonIsLoading={isAgreementActionLoading}
					firstButtonTitle={needHelp}
					firstButtonOnClick={handleGetHelp}
					firstButtonDisabled={isFaqLoading}
					firstButtonIsLoading={isFaqLoading}
					canStacks>
					<FormControlLabel
						control={
							<Checkbox
								checked={checkboxState}
								onChange={onToggleCheckbox}
								sx={{py: 0, ml: 1}}
							/>
						}
						label={notShowAgain}
					/>
				</Drawer.Footer>
			</Drawer.Root>

			<Snackbar
				message={tryAgainLater}
				open={isErrorState}
				onClose={() => setIsErrorState(false)}
				sx={{
					mb: isMobile ? '112px' : '50px',
					zIndex: 9999,
					mr: isMobile ? undefined : -1.5,
				}}
			/>

			{isGndiSaoPaulo ? (
				<IFrameModal
					openIframe={openFaqIframe}
					setOpenIframe={setOpenFaqIFrame}
					title={faq.cardTitle}
					url={faqData?.url || ''}
				/>
			) : null}
		</Fragment>
	);
}
